import { inject, Injectable, NgZone, OnDestroy } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { NotificationsService } from "@bitwarden/common/abstractions/notifications.service";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";

import { CreditsService } from "../syncing/gloss-credits/credits.service";
import { GlossCreditsTransactionSyncService } from "../syncing/gloss-credits/gloss-credits-transaction-sync.service";
import { ReferralApi } from "../DataService/referral/referral.api";

@Injectable({
  providedIn: "root",
})
export class GlossNotificationHandlerService implements OnDestroy {
  private destroy$ = new Subject<void>();

  /** Emits whenever a referrer credit is successfully applied, so UI (e.g. the
   * "N friends referred" counter on the Credits page) can refresh its data. */
  private _referralCreditApplied$ = new Subject<void>();
  readonly referralCreditApplied$ = this._referralCreditApplied$.asObservable();

  /** Coalesces overlapping drain triggers so credits are claimed once per tab. */
  private _drainInFlight: Promise<void> | null = null;

  private notificationsService = inject(NotificationsService);
  private creditsService = inject(CreditsService);
  private globalService = inject(GlobalService);
  private referralApi = inject(ReferralApi);
  private glossCreditsTransactionSync = inject(GlossCreditsTransactionSyncService);
  private ngZone = inject(NgZone);
  private logService = inject(LogService);

  constructor() {
    this.notificationsService.referralCreditAwarded$
      ?.pipe(takeUntil(this.destroy$))
      .subscribe(() => this._onReferralCreditAwarded());

    document.addEventListener("visibilitychange", this._onVisibilityChange);
  }

  private _onVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      void this.drainPendingReferralCredits();
    }
  };

  /**
   * A `type 200` WS push is a PING, not a deposit instruction. The amount in the
   * payload is intentionally ignored: depositing it directly would (a) let a
   * forged/replayed push mint arbitrary credits and (b) double-credit because
   * SignalR fans the push to every open tab — each would deposit independently.
   * Instead we drain the server-side pending-credit queue (atomic claim + per-
   * credit ack), which is the single source of truth across tabs and sources.
   *
   * NOTE (cross-repo, gloss-api #179): this requires the referrer award to
   * ALWAYS be queued as a pending credit, not only when the referrer is offline.
   * Otherwise an online referrer receives the ping but finds nothing to drain.
   */
  private async _onReferralCreditAwarded(): Promise<void> {
    await this.drainPendingReferralCredits();
  }

  /**
   * Canonical drain. Coalesces concurrent triggers (WS ping, tab
   * visibilitychange, bootstrap, credits page) so the same credits are never
   * claimed and deposited twice within one tab.
   */
  async drainPendingReferralCredits(): Promise<void> {
    if (this._drainInFlight) {
      return this._drainInFlight;
    }
    this._drainInFlight = this._withCrossTabDrainLock(() =>
      this._drainPendingReferralCredits(),
    ).finally(() => {
      this._drainInFlight = null;
    });
    return this._drainInFlight;
  }

  /**
   * Serialise the drain ACROSS browser tabs of the same origin.
   *
   * `_drainInFlight` only coalesces triggers within a single tab. The credit
   * deposit (`createTransactionOnTaskComplete`) carries no idempotency key, so
   * two tabs that concurrently claim the *same* stale (un-acked) credit would
   * each deposit it and double-credit the vault. A same-origin Web Lock makes
   * the whole claim→deposit→ack sequence mutually exclusive: by the time the
   * next tab runs, the credit is either acked (gone) or freshly re-stamped
   * (no longer stale), so it is never delivered — and therefore deposited —
   * twice.
   *
   * Residual: Web Locks are per-browser, so two *separate devices* draining the
   * same stale credit within the server stale window can still double once.
   * That self-heals (the credit is single-delivery thereafter) and is the same
   * narrow edge that only a credit-server idempotency key could fully close.
   *
   * Falls back to plain per-tab coalescing where `navigator.locks` is absent.
   */
  private async _withCrossTabDrainLock(fn: () => Promise<void>): Promise<void> {
    const locks = (globalThis.navigator as Navigator & { locks?: LockManager })?.locks;
    if (!locks?.request) {
      return fn();
    }
    try {
      await locks.request("gloss-referral-drain", async () => {
        await fn();
      });
    } catch (e) {
      // Non-fatal — e.g. the tab navigated away/unloaded while the lock was
      // pending. `fn()` itself already catches its own errors, so a rejection
      // here can only come from the Web Locks API request itself.
      this.logService.error(`[referral] cross-tab drain lock failed: ${e?.message ?? e}`);
    }
  }

  private async _drainPendingReferralCredits(): Promise<void> {
    try {
      // Claiming moves credits into a server-side "claimed" holding list rather
      // than deleting them, so only claim once the credits account is ready to
      // deposit — otherwise we'd hold credits we cannot yet apply.
      if (this.creditsService.accounts().length === 0) {
        await this.creditsService.fetch();
      }
      if (this.creditsService.accounts().length === 0) {
        this.logService.warning(
          "[referral] drain skipped: no credits account ready (plugin not enabled / fetch returned none)",
        );
        return;
      }

      const items = await this.referralApi.getPendingCredits();
      this.logService.info(`[referral] claimed ${items.length} pending credit(s)`);
      if (items.length === 0) {
        return;
      }

      let gotRefereeBonus = false;
      let gotReferrerAward = false;
      for (const item of items) {
        // Idempotency on item.id (contract step 1). The deposit itself carries no
        // idempotency key, so a credit re-delivered after a lost ack — or an app
        // restart mid-drain — would otherwise deposit a SECOND time. A durable
        // localStorage set of deposited-but-not-yet-acked ids makes redelivery a
        // safe no-op: we skip the deposit and only re-try the ack.
        const alreadyDeposited = this._isDeposited(item.id);
        if (!alreadyDeposited) {
          const applied = await this.creditsService.createTransactionOnTaskComplete(item.amount);
          if (!applied) {
            // Deposit failed — do NOT ack, do NOT mark. The credit stays claimed
            // server-side and is re-delivered on the next drain (stale-claim
            // recovery). This is the fix for the old pop-then-lose bug that
            // dropped both the referee and referrer credits on any deposit hiccup.
            this.logService.error(
              `[referral] deposit FAILED for credit ${item.id} (${item.reason}, ${item.amount}); left claimed for retry`,
            );
            continue;
          }
          // Persist BEFORE the ack: if the ack (or whole tab) dies now, the
          // redelivered credit must still be recognised as already deposited.
          this._markDeposited(item.id);
          this.logService.info(`[referral] deposited credit ${item.id} (${item.reason})`);
        }
        try {
          // Deposit has landed (now or on a prior drain) → clear it server-side.
          await this.referralApi.ackPendingCredit(item.id);
          // Acked: the server has deleted it, so it can never be re-delivered.
          // Safe to forget the id, keeping the dedup set bounded.
          this._clearDeposited(item.id);
        } catch (ackErr) {
          // Deposited but ack failed: the credit may be re-delivered after the
          // server stale window. The id stays in the dedup set so that
          // redelivery skips the deposit. Logged for reconciliation; not fatal.
          this.logService.warning(
            `Referral credit ${item.id} deposited but ack failed: ${ackErr?.message ?? ackErr}`,
          );
        }
        if (alreadyDeposited) {
          // Re-delivery of an already-credited item: ack-only, no UI/sync.
          continue;
        }
        if (item.reason === "referee_signup") {
          gotRefereeBonus = true;
        } else if (item.reason === "referrer_award") {
          gotReferrerAward = true;
        }
      }
      if (gotRefereeBonus || gotReferrerAward) {
        await this._syncAndRefresh();
        this._referralCreditApplied$.next();
      }
      if (gotRefereeBonus) {
        this.globalService.showSuccessMessage(
          "referralCreditReceived",
          "referralCreditReceivedDesc",
        );
      }
      if (gotReferrerAward) {
        this.globalService.showSuccessMessage("referralCreditAwarded", "referralCreditAwardedDesc");
      }
    } catch (e) {
      // Non-fatal — un-acked credits stay queued server-side for the next drain.
      this.logService.error(`Failed to drain pending referral credits: ${e?.message ?? e}`);
    }
  }

  /** localStorage key holding the ids of credits deposited but not yet acked. */
  private static readonly DEPOSITED_IDS_KEY = "referralDepositedCreditIds";

  private _readDepositedIds(): string[] {
    try {
      const raw = localStorage.getItem(GlossNotificationHandlerService.DEPOSITED_IDS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private _isDeposited(id: string): boolean {
    return this._readDepositedIds().includes(id);
  }

  private _markDeposited(id: string): void {
    const ids = this._readDepositedIds();
    if (ids.includes(id)) {
      return;
    }
    ids.push(id);
    localStorage.setItem(GlossNotificationHandlerService.DEPOSITED_IDS_KEY, JSON.stringify(ids));
  }

  private _clearDeposited(id: string): void {
    const ids = this._readDepositedIds().filter((x) => x !== id);
    localStorage.setItem(GlossNotificationHandlerService.DEPOSITED_IDS_KEY, JSON.stringify(ids));
  }

  private async _syncAndRefresh(): Promise<void> {
    // syncGlossCreditsTransactions fetches transactions first (which causes the Credits
    // server to settle the new balance), then calls creditsService.fetch() so
    // totalGoldCredits updates everywhere. NgZone.run() ensures Angular's change
    // detection fires immediately regardless of which page the user is on.
    await this.ngZone.run(() => this.glossCreditsTransactionSync.syncGlossCreditsTransactions());
  }

  ngOnDestroy(): void {
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    this.destroy$.next();
    this.destroy$.complete();
    this._referralCreditApplied$.complete();
  }
}
